import type {ReactElement} from 'react';
import {useState} from 'react';
import {InputField} from '@Components/input-field-v2';
import type {InputFieldControls} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType, InputFieldSize, InputFieldState} from '@Components/input-field-v2/input-field.types';
import {Text, TextSize} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import {clearInputFieldId, copyInputFieldId, getOnlineVenueErrorText} from '@Components/event-wizard/venue-online/venue-online.types';
import {updatePreviewVenue} from '@Components/event-wizard/preview/preview.helper';
import {useDispatch} from 'react-redux';
import {onEventWizardOnlineVenueChange} from '@Components/event-wizard/event-wizard-slice';
import {useEventWizardOnlineVenue} from '@Components/event-wizard/event-wizard.hooks';
import {getAssetUrl} from '@Utils/s3.util';
import {cleanUserInputURL, getIsOnlineVenueComplete} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import styles from './venue-online.module.scss';
import {EventWizardTestId} from '@/test-ids/event-wizard-test-id';

export function VenueOnline(): ReactElement<any> {
  const dispatch = useDispatch();
  const onlineVenue = useEventWizardOnlineVenue();
  const [isErrState, setIsErrState] = useState<boolean>(false);

  const updateInputField = (val: string): void => {
    setIsErrState(false);
    dispatch(onEventWizardOnlineVenueChange(val));
    updatePreviewVenue({
      onlineVenueURL: val,
    });
  };

  const onInputBlur = (): void => {
    const urlUpdated: string | undefined = cleanUserInputURL(onlineVenue);

    if (urlUpdated) {
      dispatch(onEventWizardOnlineVenueChange(urlUpdated));
      updatePreviewVenue({
        onlineVenueURL: urlUpdated,
      });
    }
    setIsErrState(!getIsOnlineVenueComplete(urlUpdated));
  };

  const getControls = (inputFieldVal: string | undefined): InputFieldControls => {
    if (!inputFieldVal || inputFieldVal === '') {
      return {
        showDefaultControls: false,
        showSingleDefaultControlOnly: false,
        customControls: [],
      };
    }

    return {
      showDefaultControls: false,
      showSingleDefaultControlOnly: false,
      onClick(iconName: string, e): void {
        e?.stopPropagation();
        void handleCopyBtnClick(iconName).then();
      },
      customControls: [
        {
          id: clearInputFieldId,
          type: InputControlItemType.ICON_BTNS,
          icon: 'icon-close',
        },
        {
          id: copyInputFieldId,
          type: InputControlItemType.ICON_BTNS,
          icon: 'icon-copy',
        },
      ],
    };
  };

  const handleCopyBtnClick = async (btnID: string): Promise<void> => {
    if (btnID === clearInputFieldId) {
      updateInputField('');
    } else if (btnID === copyInputFieldId) {
      if (onlineVenue != null) {
        await navigator.clipboard.writeText(onlineVenue);
      }
    }
  };

  const getZoomIcon = (): ReactElement<any> => {
    return <img className={`${styles.onlineIconImg} spacing-m-r-1`} src={getAssetUrl('zoom-logo.svg')} alt="zoom icon" />;
  };

  const getGoogleMeetIcon = (): ReactElement<any> => {
    return <img className={`${styles.onlineIconImg} spacing-m-r-1`} src={getAssetUrl('google-meet.svg')} alt="google meet icon" />;
  };

  const openZoomTab = (): void => {
    window.open('https://app.zoom.us/wc', '_blank');
  };

  const openGoogleMeetTab = (): void => {
    window.open('https://meet.google.com/', '_blank');
  };

  return (
    <>
      <div className="spacing-m-b-5">
        <InputField
          inputClassName={styles.input}
          inputTestId={EventWizardTestId.ONLINE_VENUE_INPUT}
          input={onlineVenue}
          onInputChange={updateInputField}
          placeholder={window.i18next.t('pmwjs_add_link_online_event_details')}
          size={InputFieldSize.XSMALL}
          controls={getControls(onlineVenue)}
          onInputBlur={onInputBlur}
          state={isErrState ? InputFieldState.ERROR : InputFieldState.DEFAULT}
          information={isErrState ? getOnlineVenueErrorText(onlineVenue) : undefined}
        />
      </div>
      <Text val={window.i18next.t('pmwjs_create_new_link')} size={TextSize.XSMALL} bold />
      <div className="flex-row-align-center spacing-m-t-2">
        <Button onClick={openZoomTab} customClasses="spacing-m-r-2" size={Size.XSMALL} type={Type.SECONDARY} text="Zoom">
          {getZoomIcon()}
        </Button>
        <Button onClick={openGoogleMeetTab} size={Size.XSMALL} type={Type.SECONDARY} text="Google Meet">
          {getGoogleMeetIcon()}
        </Button>
      </div>
    </>
  );
}
